import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import * as TWEEN from "@tweenjs/tween.js";
import GameScene, { dragControls, eraSchemas } from "./Scene";
import "./App.css";
import { LIBRARY, Metadata } from "./Objects/Constants";
import GenericObject from "./Objects/GenericObject";
// import Dialog from "@mui/material/Dialog"
// import DialogTitle from "@mui/material/DialogTitle"
// import DialogContent from "@mui/material/DialogContent"
// import DialogActions from "@mui/material/DialogActions"
// import Button from "@mui/material/Button"
// import Stack from "@mui/material/Stack"

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useBoolean } from "./use-boolean";

export let controls: OrbitControls;

type ColorScheme = {
  [key: string]: string;
  initial: string;
  negative: string; // Changed from 'neg' for clarity
  positive: string; // Changed from 'pos' for clarity
  special: string; // Changed from 'pos' for clarity
};

const type_color: ColorScheme = {
  initial: "#FCF4B3F0",
  negative: "#F0A090F0",
  positive: "#A2E0A0F0",
  special: "#CAE4F1",
};

export const WIN_CONDITION = [
  "recycling_facility",
  "electric_car",
  "community_garden",
];

export const typeOrder = [
  "special",
  "initial",
  "neutral",
  "negative",
  "positive",
];

type BadgeColor = "gray" | "red" | "yellow" | "green";

// Define styles for each color
const badgeStyles: Record<BadgeColor, React.CSSProperties> = {
  gray: {
    backgroundColor: "#f7fafc", // bg-gray-100
    color: "#4a5568", // text-gray-600
  },
  red: {
    backgroundColor: "#fed7d7", // bg-red-100
    color: "#c53030", // text-red-700
  },
  yellow: {
    backgroundColor: "#fefcbf", // bg-yellow-100
    color: "#975a16", // text-yellow-800
  },
  green: {
    backgroundColor: "#c6f6d5", // bg-green-100
    color: "#2f855a", // text-green-700
  },
};

// Badge component with color prop
interface BadgeProps {
  color?: BadgeColor;
  children: ReactNode;
  onClick?: () => void;
}

const Badge: React.FC<BadgeProps> = ({ color = "gray", children, onClick }) => {
  const style: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "0.375rem",
    padding: "0.125rem 0.375rem",
    fontSize: "0.75rem",
    fontWeight: 500,
    ...badgeStyles[color], // Safely spread color-specific styles
    cursor: onClick ? "pointer" : undefined, // Change cursor style if onClick is provides
  };

  return <span style={style}>{children}</span>;
};

function App() {
  const divRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<GameScene | null>(null);
  const initialObjects = ["air", "fire", "water", "earth"];
  const [unlockedObjects, setUnlockedObjects] = useState(initialObjects);

  const [hoveredType, setHoveredType] = useState<string | undefined>();
  const [era, setEra] = useState<number>(0); // For library items
  const [showPositive, setShowPositive] = useState<boolean>(true);
  const [showNegative, setShowNegative] = useState<boolean>(true);
  const [showNeutral, setShowNeutral] = useState<boolean>(true);
  const [objOrder, setObjOrder] = useState<"By category" | "By recency">(
    "By category"
  );
  // const showWelcomeDialog = useBoolean(true)

  const healthLevel = Math.max(
    0.1,
    Math.min(
      1,
      unlockedObjects.reduce((prev, typeId) => {
        const metadata = LIBRARY[typeId]!;
        if (metadata.type === "negative") {
          return (prev -= 0.5);
        } else if (metadata.type === "positive") {
          return (prev += 0.2);
        } else {
          return prev;
        }
      }, 1)
    )
  );

  useEffect(() => {
    if (!divRef.current) return;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.shadowMap.enabled = true;
    renderer.setSize(divRef.current.clientWidth, divRef.current.clientHeight);
    renderer.localClippingEnabled = true;
    divRef.current.appendChild(renderer.domElement);
    renderer.domElement.addEventListener("dragover", (e) => e.preventDefault());

    renderer.domElement.addEventListener("drop", (e) => {
      const objectId = e.dataTransfer?.getData("text");
      const mousePosition = new THREE.Vector2(
        (e.clientX / renderer.domElement.clientWidth) * 2 - 1,
        (1 - e.clientY / renderer.domElement.clientHeight) * 2 - 1
      );
      if (objectId) {
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mousePosition, camera);
        const intersection = raycaster.ray.intersectPlane(
          new THREE.Plane(new THREE.Vector3(0, 1, 0)),
          new THREE.Vector3()
        );
        if (intersection) {
          intersection.z = Math.max(0, intersection.z);
          const newObject = new GenericObject(objectId);
          scene.addObject(newObject, intersection).then(async (asset) => {
            scene.dragEnd(asset, true);
            if (asset.parent) {
              scene.updateDragControl();
              dragControls.deactivate();
              dragControls.activate();
            }
          });
        }
      }
    });
    // divRef.current.appendChild( XRButton.createButton( renderer ) );
    // divRef.current.appendChild( VRButton.createButton(renderer) );
    // renderer.xr.getCamera().up.set(0, 0, 1)
    // renderer.xr.getCamera().position.set(0, -4, 1.5)
    // renderer.xr.enabled = true

    const camera = new THREE.PerspectiveCamera(
      80,
      divRef.current.clientWidth / divRef.current.clientHeight,
      0.1,
      1000
    );
    const scene = new GameScene(camera, renderer);
    scene.setHoveredItem = setHoveredType;
    scene.setEra = setEra;
    scene.eraOrder = era;
    sceneRef.current = scene;
    camera.up.set(0, 0, 1);
    camera.lookAt(new THREE.Vector3(0, 0, 2));
    camera.position.set(0, -6, 4);

    controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 0, 2);

    function animate() {
      requestAnimationFrame(animate);
      controls.update();
      scene.animate();
      renderer.render(scene, camera);
    }

    animate();

    renderer.setPixelRatio(Math.min(1.2, window.devicePixelRatio));

    scene.background = new THREE.Color(0xd8f2ff);

    const handleResize = () => {
      const w = divRef.current?.clientWidth;
      const h = divRef.current?.clientHeight;

      if (w === undefined || h === undefined) {
        return;
      }

      camera.aspect = w / h;

      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };

    window.addEventListener("resize", () => handleResize());
  }, []);

  useEffect(() => {
    if (sceneRef.current) {
      sceneRef.current.unlockObject = (type) => {
        if (!unlockedObjects.includes(type)) {
          setUnlockedObjects(unlockedObjects.concat(type));
          return true;
        } else {
          return false;
        }
      };
    }
  }, [unlockedObjects]);

  useEffect(() => {
    const { newObjects, message, name } = eraSchemas[era];
    // Toast for new era
    toast(name + ": " + message, {
      position: "top-center",
      autoClose: 10000,
      theme: "colored",

      className: "toast-overlay",
    });

    if (era > 0) {
      // Update the list of unlocked objects
      setUnlockedObjects((prevObjects) => [...newObjects, ...prevObjects]);

      // Toast for each new object
      newObjects.forEach((objectName) => {
        toast.info(`Unlock: ${LIBRARY[objectName].name}`);
      });
    }
  }, [era]);

  return (
    <div
      ref={divRef}
      style={{ width: "100vw", height: "100vh", position: "absolute" }}
    >
      <ToastContainer position="top-left" />
      <div className="status-box">
        <div className="progress-label">Environment Health</div>
        <HealthBar health={healthLevel} />
        <div style={{ height: 5 }} />
        <div className="progress-label">Progress: {eraSchemas[era].name}</div>
        <HealthBar health={era / Object.keys(eraSchemas).length} />
      </div>

      {unlockedObjects.length === initialObjects.length && (
        <div className="center-overlay">
          <h2>🌿 Welcome to EcoAlchemy! 🌿</h2>
          <p>
            Let's learn the various elements found on Earth and how they
            contribute to the environment we live in. Ready to explore and make
            a difference? Let's get started by combining 2 elements together!
          </p>
        </div>
      )}

      {unlockedObjects.length === initialObjects.length + 1 && (
        <div className="center-overlay">
          <h2>🎉 You've unlocked Elements Library. 🎉</h2>
          <p>
            {" "}
            Monitor your Progress and Health Level as you explore. Remember,
            elements impact the environment differently. Ask your{" "}
            <a href="https://ai-me.app/ecoalchemy" target="_blank">
              EcoAlchemy AI Tutor Agent
            </a>{" "}
            for help!
          </p>
        </div>
      )}

      {unlockedObjects.length === Object.keys(LIBRARY).length && (
        <div className="center-overlay">
          <h2>🎉 Congratulations! 🎉</h2>
          <p>
            You have unlocked all elements in the library! Your journey has led
            to a deeper understanding of the complex interplay between different
            elements and their impact on the environment. This is a significant
            achievement in your quest for knowledge and harmony with nature.
            Thank you for your dedication and efforts in exploring and
            protecting our world.{" "}
          </p>
        </div>
      )}

      {WIN_CONDITION.every((condition) =>
        unlockedObjects.includes(condition)
      ) && (
        <div className="center-overlay">
          <h2>🎉 Congratulations! 🎉</h2>
          <p>
            You have discovered the path towards a sustainable future! Thank you
            for playing our game, if you have any feedback, please share them
            here.
          </p>
          <a href="https://ai-me.app/ecoalchemy" target="_blank">
            Feedback
          </a>
          <p>Feel free to keep exploring the other elements!</p>
        </div>
      )}

      {hoveredType && (
        <div className="hover-container">
          <div
            style={{
              textAlign: "center",
              fontWeight: 600,
              marginBottom: "5px",
            }}
          >
            {LIBRARY[hoveredType].name}
          </div>
          <div style={{ fontSize: 14, textAlign: "center" }}>
            {LIBRARY[hoveredType].detail_description}
          </div>
        </div>
      )}

      {unlockedObjects.length > initialObjects.length && (
        <div className="object-library">
          <h4>{`Elements Library (${unlockedObjects.length} / ${
            Object.keys(LIBRARY).length
          })`}</h4>
          <div
            style={{
              display: "inline-flex",
              flexDirection: "row",
              padding: "10px",
              gap: "10px",
            }}
          >
            <Badge color="yellow">Initial</Badge>
            {/* <Badge color="gray"> */}
            <Badge color="gray" onClick={() => setShowNeutral(!showNeutral)}>
              Neutral
            </Badge>
            <Badge color="red" onClick={() => setShowNegative(!showNegative)}>
              Negative
            </Badge>
            <Badge color="green" onClick={() => setShowPositive(!showPositive)}>
              {" "}
              Positive
            </Badge>
          </div>
          <div className="library-table">
            {unlockedObjects
              .sort(
                (a, b) =>
                  typeOrder.indexOf(LIBRARY[a].type) -
                  typeOrder.indexOf(LIBRARY[b].type)
              )
              .map((obj) => {
                return (
                  <div
                    className="item"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer?.setData("text/plain", obj);
                    }}
                    style={{ backgroundColor: type_color[LIBRARY[obj].type] }}
                    onMouseEnter={() => setHoveredType(obj)}
                    onMouseLeave={() => setHoveredType(undefined)}
                  >
                    <img
                      src={`/images/original_images/${obj}.jpg`}
                      width={40}
                      draggable={false}
                    />
                    <div
                      className="item-details"
                      style={{
                        flexGrow: 1,
                        minWidth: 0 /* Ensures it can shrink */,
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: 17 }}>
                        {LIBRARY[obj].name}
                      </div>
                      <div
                        style={{
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          fontSize: "13px",
                          maxWidth: "100%", // Ensure this element cannot exceed its parent’s width
                        }}
                      >
                        {LIBRARY[obj].short_description}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          <p>
            Feeling stuck? Ask{" "}
            <a href="https://ai-me.app/ecoalchemy" target="_blank">
              EcoAlchemy AI Tutor Agent
            </a>{" "}
            for help!
          </p>
        </div>
      )}
    </div>
  );
}

function mapHealthToColor(health: number): THREE.Color {
  // Clamp health value between 0 and 1
  health = Math.max(0, Math.min(1, health));

  // Define RGB values for green and dark red
  const green = new THREE.Color(0x00e010);
  const darkRed = new THREE.Color(0x8b0000);

  // Interpolate between green and dark red based on health value
  return darkRed.clone().lerp(green, health);
}

function HealthBar(props: { health: number }) {
  const color = mapHealthToColor(props.health);
  return (
    <div
      className="progress-track"
      style={{ border: `2px solid #${color.getHexString()}80` }}
    >
      <div
        className="progress-bar"
        style={{
          width: `${props.health * 100}%`,
          backgroundColor: `#${color.getHexString()}`,
        }}
      />
    </div>
  );
}

export default App;
